import { useCallback, useContext, useEffect, useState } from "react";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Select,
  MenuItem
} from "@mui/material";
import {
  ArrowUpward,
  ArrowDownward,
  Visibility
} from "@mui/icons-material";
import dayjs from "dayjs";
import UserContext from "../Contexts/UserContext";
import {
  API_GetAllLeaveRequests,
  API_GetCandidateByID
} from "../Assets/ApiUrlContainer";
import { default as axios } from "../axiosConfig";
import LeaveRequestDetails from "./LeaveRequestDetails";
import { Pagination } from "./Pagination";

const AdminLeaveRequests = () => {
  const currentUser = useContext(UserContext);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortField, setSortField] = useState("StartDate");
  const [jobs, setJobs] = useState([]);
  const [managerID, setManagerID] = useState("");
  const [candidateNames, setCandidateNames] = useState({});
  const [start, setStart] = useState(0);
  const [limit, setLimit] = useState(10);
  const [activeTab, setActiveTab] = useState("current");
  const userEmail = currentUser.user.Email;
  const isTGS = currentUser.user.Role === "admin" || currentUser.user.Role === "tgs-admin";

  const convertDateForDayJS = (dateToConvert) => {
    let date = new Date(dateToConvert);
    return `${date.getUTCFullYear()}-${("0" + (date.getUTCMonth() + 1)).slice(-2)}-${("0" + date.getUTCDate()).slice(-2)}`;
  }

  const fetchAllLeaveRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_GetAllLeaveRequests, { params: { Email: userEmail, Role: currentUser.user.Role } });
      const { LeaveRequests, Jobs, ManagerId } = res.data;

      const sortFunc = (a, b) => {
        const getValue = (obj) => {
          if (sortField === "Job") {
            const jobName = Jobs.find((j) => j.Id === obj.JobId)?.Name || "";
            return jobName.toLowerCase();
          }
          return dayjs(obj[sortField]);
        };

        const aVal = getValue(a);
        const bVal = getValue(b);

        if (sortField === "Job") {
          return sortOrder === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        return sortOrder === "asc" ? aVal.diff(bVal) : bVal.diff(aVal);
      };

      setLeaveRequests(LeaveRequests.sort(sortFunc));
      setJobs(Jobs);
      setManagerID(ManagerId || "");

    } catch (error) {
      console.error("Error fetching leave requests:", error);
    } finally {
      setLoading(false);
    }
  }, [userEmail, sortField, sortOrder, currentUser.user.Role]);

  useEffect(() => {
    if (userEmail) {
      fetchAllLeaveRequests();
    }
  }, [userEmail, fetchAllLeaveRequests]);

  useEffect(() => {
    const uniqueCandidateIDs = [
      ...new Set(leaveRequests.map((lr) => lr.CandidateId)),
    ];

    uniqueCandidateIDs.forEach((id) => {
      if (!candidateNames[id]) {
        axios
          .get(`${API_GetCandidateByID}/${id}`)
          .then((res) => setCandidateNames((prev) => ({ ...prev, [id]: res.data.Name || "Unknown" })))
          .catch((err) => console.error("Error fetching candidate name:", err));
      }
    });
  }, [leaveRequests, candidateNames]);

  const filteredLeaveRequests = leaveRequests.filter((lr) => {
    if (lr.Status === "rejected") return false;
    return activeTab === "archived"
      ? lr.Status === "archived"
      : lr.Status !== "archived";
  });

  return (
    <div className="">
      <div className="tasksContainer">
        {selectedLeaveRequest ? (
          <LeaveRequestDetails
            leaveRequest={selectedLeaveRequest}
            candidateId={selectedLeaveRequest.CandidateId}
            managerId={managerID}
            jobId={selectedLeaveRequest.JobId}
            jobName={jobs.find((job) => job.Id === selectedLeaveRequest.JobId)?.Name || "Unknown Job"}
            onBack={() => {
              setSelectedLeaveRequest(null);
              fetchAllLeaveRequests();
            }}
            isTGS={isTGS}
            readOnlyCalendar={true}
          />
        ) : (
          <>
            {/* Header Section */}
            <div className="tasksHeader">
              {/* Filter Dropdown */}
              <div className="sortingContainer">
                <label>Sort By:</label>
                <Select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ ml: 2 }}
                >
                  <MenuItem value="Start_Date">Start Date</MenuItem>
                  <MenuItem value="Submitted_date">Submission Date</MenuItem>
                  <MenuItem value="Approving_manager_ID">
                    Approval Status
                  </MenuItem>
                  <MenuItem value="Job">Job</MenuItem>
                </Select>
                <IconButton
                  onClick={() =>
                    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
                  }
                  size="small"
                  sx={{
                    ml: 1,
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                    padding: "4px",
                  }}
                >
                  {sortOrder === "desc" ? (
                    <ArrowUpward fontSize="small" />
                  ) : (
                    <ArrowDownward fontSize="small" />
                  )}
                </IconButton>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <h3 style={{ marginRight: "20px" }}>Leave Requests</h3>
              <div className="leaveRequestFilter">
                <Button
                  variant={activeTab === "current" ? "contained" : "outlined"}
                  onClick={() => setActiveTab("current")}
                  sx={{
                    mr: 1,
                    backgroundColor:
                      activeTab === "current" ? "#673ab7" : "white",
                    color: activeTab === "current" ? "white" : "#673ab7",
                    borderColor: "#673ab7",
                  }}
                >
                  Current
                </Button>
                <Button
                  variant={activeTab === "archived" ? "contained" : "outlined"}
                  onClick={() => setActiveTab("archived")}
                  sx={{
                    backgroundColor:
                      activeTab === "archived" ? "#673ab7" : "white",
                    color: activeTab === "archived" ? "white" : "#673ab7",
                    borderColor: "#673ab7",
                  }}
                >
                  Archived
                </Button>
              </div>
            </div>

            {loading ? (
              <CircularProgress />
            ) : (
              <TableContainer component={Paper}>
                <Table sx={{ tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "20%" }}>
                        <strong>Dates</strong>
                      </TableCell>
                      {/* <TableCell sx={{ width: '20%' }}><strong>Job</strong></TableCell> */}
                      <TableCell>
                        <strong>Name</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Reason</strong>
                      </TableCell>
                      <TableCell sx={{ width: "14%" }}>
                        <strong>Status</strong>
                      </TableCell>
                      <TableCell sx={{ width: "12%" }}>
                        <strong>Submission Date</strong>
                      </TableCell>
                      <TableCell sx={{ width: "5%" }} align="center">
                        <strong>Actions</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredLeaveRequests
                      .slice(start, limit)
                      .map((lr) => (
                        <TableRow
                          key={`${lr.Id}`}
                          sx={{
                            backgroundColor:
                              lr.Status === "rejected" ? "#FFEBEE" : "inherit",
                          }}
                        >
                          <TableCell>
                            {dayjs(convertDateForDayJS(lr.StartDate)).format("MMM D, YYYY") +
                              " - " +
                              dayjs(convertDateForDayJS(lr.EndDate)).format("MMM D, YYYY")}
                          </TableCell>

                          <TableCell>
                            {candidateNames[lr.CandidateId] || "Loading..."}
                          </TableCell>

                          <TableCell>{lr.TypeOfLeave}</TableCell>

                          <TableCell>
                            {lr.Status === "approved"
                              ? "Approved"
                              : lr.Status === "submitted"
                                ? "Pending Approval"
                                : lr.Status === "rejected"
                                  ? "Rejected"
                                  : lr.Status === "cancelled"
                                    ? "Cancelled"
                                    : lr.Status === "archived"
                                      ? "Archived"
                                      : "Draft"}
                          </TableCell>

                          <TableCell>
                            {dayjs(convertDateForDayJS(lr.SubmittedDate)).format("MMM D, YYYY")}
                          </TableCell>

                          {/* 👁️ Eye icon column */}
                          <TableCell align="center">
                            <IconButton
                              variant="outlined"
                              onClick={() => setSelectedLeaveRequest(lr)}
                              sx={{ color: "#673ab7", borderColor: "#673ab7" }}
                            >
                              <Visibility />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                <Pagination
                  totalNumItems={filteredLeaveRequests.length}
                  onPageChangeNewPosition={setStart}
                  onRowsChangeNewDisplayLimit={setLimit}
                />
              </TableContainer>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminLeaveRequests;
